import { motion } from "framer-motion";
import { HeartHandshake, GraduationCap, Compass, Baby } from "lucide-react";

const IMAGES = {
  education:
    "https://images.unsplash.com/photo-1570657891791-e39a9d185540?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHw0fHxkaXZlcnNlJTIwbW90aGVyJTIwbmV3Ym9ybiUyMGJhYnklMjBjYXJlJTIwbWF0ZXJuYWwlMjBoZWFsdGglMjBkb3VsYSUyMGNvYWNoaW5nfGVufDB8fHx8MTc5MjAwMjYyMnww&ixlib=rb-4.1.0&q=85",
  coaching:
    "https://images.unsplash.com/photo-1583710457367-47de0ea21fef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwbW90aGVyJTIwbmV3Ym9ybiUyMGJhYnklMjBjYXJlJTIwbWF0ZXJuYWwlMjBoZWFsdGglMjBkb3VsYSUyMGNvYWNoaW5nfGVufDB8fHx8MTc5MjAwMjYyMnww&ixlib=rb-4.1.0&q=85",
  postpartum:
    "https://images.unsplash.com/photo-1542385151-efd9000785a0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwzfHxkaXZlcnNlJTIwbW90aGVyJTIwbmV3Ym9ybiUyMGJhYnklMjBjYXJlJTIwbWF0ZXJuYWwlMjBoZWFsdGglMjBkb3VsYSUyMGNvYWNoaW5nfGVufDB8fHx8MTc5MjAwMjYyMnww&ixlib=rb-4.1.0&q=85",
};

const reveal = {
  hidden: { opacity: 0, y: 36 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.12, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  }),
};

const TAG_STYLES = {
  coral: "bg-[#F2644C]/10 text-[#F2644C]",
  blue: "bg-[#2563EB]/10 text-[#2563EB]",
  purple: "bg-[#6D28D9]/10 text-[#6D28D9]",
  yellow: "bg-[#F5B301]/15 text-[#A17503]",
  dark: "bg-[#F2644C] text-white",
};

const Tag = ({ children, tone = "coral" }) => (
  <span
    className={`inline-block text-[10px] font-heading font-bold uppercase tracking-[0.18em] px-3 py-1 rounded-full mb-5 ${TAG_STYLES[tone]}`}
  >
    {children}
  </span>
);

const ICON_TONES = {
  coral: "bg-[#F2644C]/10 text-[#F2644C] group-hover:bg-[#F2644C] group-hover:text-white",
  blue: "bg-[#2563EB]/10 text-[#2563EB] group-hover:bg-[#2563EB] group-hover:text-white",
  purple: "bg-[#6D28D9]/10 text-[#6D28D9] group-hover:bg-[#6D28D9] group-hover:text-white",
  yellow: "bg-[#F5B301]/15 text-[#A17503] group-hover:bg-[#F5B301] group-hover:text-white",
  dark: "bg-white/10 text-[#F6F2FF]",
};

const CardShell = ({ testid, i, className = "", children }) => (
  <motion.article
    data-testid={testid}
    custom={i}
    variants={reveal}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-80px" }}
    whileHover={{ y: -6 }}
    transition={{ type: "spring", stiffness: 220, damping: 22 }}
    className={`group rounded-2xl overflow-hidden transition-shadow duration-500 hover:shadow-xl ${className}`}
  >
    {children}
  </motion.article>
);

const IconBadge = ({ icon: Icon, tone = "coral" }) => (
  <div
    className={`h-11 w-11 rounded-xl flex items-center justify-center mb-5 transition-colors duration-500 ${ICON_TONES[tone]}`}
  >
    <Icon className="h-5 w-5 transition-colors duration-500" />
  </div>
);

const ServicesSection = () => (
  <section id="services" data-testid="services-section" className="py-20 lg:py-28">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
      <motion.div
        variants={reveal}
        custom={0}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-80px" }}
        className="max-w-3xl mb-16"
      >
        <p className="text-xs uppercase tracking-[0.25em] font-heading font-bold text-[#F2644C] mb-4">
          What we offer
        </p>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#2B1B45] leading-tight">
          Full-Spectrum Doula &amp;{" "}
          <span className="font-serif italic font-semibold text-[#6D28D9]">
            Health Coaching
          </span>{" "}
          Services
        </h2>
        <p className="mt-5 text-base sm:text-lg text-[#5B5470] leading-relaxed">
          Tailored, evidence-based care through every phase of your journey.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 lg:grid-rows-2 gap-6">
        <CardShell
          testid="service-card-full-spectrum-doula"
          i={1}
          className="md:col-span-2 lg:col-span-1 lg:row-span-2 bg-[#2E1B4D] border border-white/10"
        >
          <div className="h-full min-h-[420px] p-8 sm:p-10 flex flex-col justify-between">
            <div>
              <Tag tone="dark">Core Offer</Tag>
              <IconBadge icon={HeartHandshake} tone="dark" />
              <h3 className="font-heading text-2xl sm:text-3xl font-extrabold text-white mb-4 tracking-tight">
                Full-Spectrum Doula Care
              </h3>
              <p className="text-sm sm:text-base text-[#CFC7E6] leading-relaxed">
                Continuous physical, emotional, and informational support
                before, during, and immediately after childbirth. Wherever and
                however you choose to welcome your baby, you will not do it
                alone.
              </p>
            </div>
            <p className="mt-10 font-serif italic text-xl text-[#F5B301]">
              "Steady support, start to finish."
            </p>
          </div>
        </CardShell>

        <CardShell
          testid="service-card-childbirth-education"
          i={2}
          className="bg-white border border-[#E6E0F0] shadow-sm"
        >
          <div className="h-40 overflow-hidden">
            <img
              src={IMAGES.education}
              alt="Childbirth education session"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          </div>
          <div className="p-7 sm:p-8">
            <Tag tone="blue">ICEA-Trained Educator</Tag>
            <IconBadge icon={GraduationCap} tone="blue" />
            <h3 className="font-heading text-xl sm:text-2xl font-bold text-[#2B1B45] mb-3 tracking-tight">
              Childbirth Education
            </h3>
            <p className="text-sm sm:text-base text-[#5B5470] leading-relaxed">
              Evidence-based classes led by an ICEA-trained childbirth
              educator: birth planning, pain management techniques, advocacy
              preparation, and partner involvement.
            </p>
          </div>
        </CardShell>

        <CardShell
          testid="service-card-health-coaching"
          i={3}
          className="bg-white border border-[#E6E0F0] shadow-sm"
        >
          <div className="h-40 overflow-hidden">
            <img
              src={IMAGES.coaching}
              alt="Maternal health coaching and support"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          </div>
          <div className="p-7 sm:p-8">
            <Tag tone="purple">Certified INHC/CIHC</Tag>
            <IconBadge icon={Compass} tone="purple" />
            <h3 className="font-heading text-xl sm:text-2xl font-bold text-[#2B1B45] mb-3 tracking-tight">
              Maternal Health Coaching
            </h3>
            <p className="text-sm sm:text-base text-[#5B5470] leading-relaxed">
              Holistic prenatal and postpartum wellness strategies, nutrition
              optimization, and hormonal recovery coaching.
            </p>
          </div>
        </CardShell>

        <CardShell
          testid="service-card-postpartum-support"
          i={4}
          className="lg:col-span-2 bg-white border border-[#E6E0F0] shadow-sm"
        >
          <div className="grid sm:grid-cols-2 h-full">
            <div className="h-48 sm:h-full overflow-hidden">
              <img
                src={IMAGES.postpartum}
                alt="Mother holding newborn baby"
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <div className="p-7 sm:p-8">
              <Tag tone="yellow">Fourth Trimester</Tag>
              <IconBadge icon={Baby} tone="yellow" />
              <h3 className="font-heading text-xl sm:text-2xl font-bold text-[#2B1B45] mb-3 tracking-tight">
                Postpartum Support
              </h3>
              <p className="text-sm sm:text-base text-[#5B5470] leading-relaxed">
                In-home newborn care guidance, lactation setup, physical
                recovery support, and emotional check-ins, so no one navigates
                the fourth trimester alone.
              </p>
            </div>
          </div>
        </CardShell>
      </div>
    </div>
  </section>
);

export default ServicesSection;
